import React from "react";
import {AbsoluteFill, useCurrentFrame, interpolate, spring, Audio, staticFile, Sequence} from "remotion";
import {CLAMP, FONT_STACK, IDV} from "../presets/idv";
import {AccentLine} from "../components/dimus/AccentLine";
import {KineticText} from "../components/text/KineticText";
import {Noise} from "../components/dimus/Noise";
import {Vignette} from "../components/dimus/Vignette";
import {FilmGrain} from "../components/dimus/FilmGrain";

/**
 * VERDADES DIFÍCEIS #09 — "Briefing Ruim com Competência"
 * 5 cenas | Editing Style: DYNAMIC | TTS ElevenLabs Guilherme
 */
const SCENES = [
  {dur: 135, file: "audio/verdades09/c01.mp3"},
  {dur: 135, file: "audio/verdades09/c02.mp3"},
  {dur: 159, file: "audio/verdades09/c03.mp3"},
  {dur: 189, file: "audio/verdades09/c04.mp3"},
  {dur: 153, file: "audio/verdades09/c05.mp3"},
];
const TRANSITION_DUR = 25;
export const VERDADES09_TOTAL = SCENES.reduce((s,sc)=>s+sc.dur,0) + (SCENES.length-1)*TRANSITION_DUR;

const YELLOW = IDV.third, GREEN = IDV.secondary, BLUE = IDV.primary, RED = IDV.error;
const WHITE = "#f5f5f7", MUTED = "#a3a3a7";
const TTS_VOLUME = 0.95;

const SceneFade: React.FC<{duration:number;children:React.ReactNode}> = ({duration,children}) => {
  const f = useCurrentFrame();
  const op = Math.min(interpolate(f,[0,12],[0,1],CLAMP), interpolate(f,[duration-15,duration],[1,0],CLAMP));
  const floatY = interpolate(f,[0,60],[0,4],CLAMP);
  return <AbsoluteFill style={{opacity:op,transform:`translateY(${floatY}px)`}}>{children}</AbsoluteFill>;
};

const S1 = () => {
  const f = useCurrentFrame();
  const sp = spring({frame:f-8,fps:30,config:{damping:18,stiffness:110}});
  return (
    <AbsoluteFill style={{background:`radial-gradient(ellipse at 50% 35%, ${RED}22, #000 70%)`}}>
      <Audio src={staticFile("audio/verdades09/c01.mp3")} volume={TTS_VOLUME}/>
      <div style={{position:"absolute",top:300,left:80,right:80,transform:`scale(${0.92+0.08*sp})`,opacity:sp}}>
        <div style={{fontFamily:FONT_STACK,color:MUTED,fontSize:36,fontWeight:500,letterSpacing:2,marginBottom:24}}>VERDADES DIFÍCEIS #09</div>
        <AccentLine color={RED} delay={20}/>
        <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:84,fontWeight:800,lineHeight:1.08,marginTop:40}}>
          A maioria das agências entrega <span style={{color:RED}}>briefing ruim</span>.
        </div>
      </div>
      <div style={{position:"absolute",top:1050,left:80,right:80}}>
        <KineticText text="Com uma competência impressionante." startFrame={70} color={YELLOW} fontSize={72} fontFamily={FONT_STACK}/>
      </div>
    </AbsoluteFill>
  );
};

const S2 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background:"#0a0a0a"}}>
      <Audio src={staticFile("audio/verdades09/c02.mp3")} volume={TTS_VOLUME}/>
      <div style={{position:"absolute",top:260,left:80,right:80}}>
        <div style={{fontFamily:FONT_STACK,color:MUTED,fontSize:36,letterSpacing:2,marginBottom:16}}>A RAIZ</div>
        <AccentLine color={RED} delay={6}/>
        <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:68,fontWeight:800,marginTop:60,lineHeight:1.15,opacity:interpolate(f,[18,50],[0,1],CLAMP)}}>
          Briefing ruim não é<br/>culpa da agência.
        </div>
        <div style={{marginTop:50,padding:40,borderRadius:24,background:`${BLUE}40`,border:`2px solid ${RED}`,opacity:interpolate(f,[60,95],[0,1],CLAMP)}}>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:50,fontWeight:600,lineHeight:1.3}}>
            É quando <span style={{color:YELLOW}}>você</span> não entende a fundo sua <span style={{color:YELLOW}}>oferta</span> nem o seu <span style={{color:GREEN}}>cliente</span>.
          </div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const S3 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background:`radial-gradient(ellipse at 50% 50%, ${RED}18, #000 75%)`}}>
      <Audio src={staticFile("audio/verdades09/c03.mp3")} volume={TTS_VOLUME}/>
      <div style={{position:"absolute",top:340,left:80,right:80}}>
        <div style={{fontFamily:FONT_STACK,color:RED,fontSize:40,letterSpacing:2,marginBottom:16}}>O PARADOXO</div>
        <AccentLine color={RED} delay={6}/>
        <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:74,fontWeight:800,marginTop:60,lineHeight:1.15,opacity:interpolate(f,[20,55],[0,1],CLAMP)}}>
          Quanto <span style={{color:RED}}>mais competente</span> a agência...
        </div>
        <div style={{marginTop:50,fontFamily:FONT_STACK,color:WHITE,fontSize:64,fontWeight:700,lineHeight:1.2,opacity:interpolate(f,[100,140],[0,1],CLAMP)}}>
          mais ela <span style={{color:YELLOW}}>amplifica a sua confusão</span>.
        </div>
        <div style={{marginTop:40,fontFamily:FONT_STACK,color:MUTED,fontSize:46,opacity:interpolate(f,[140,170],[0,1],CLAMP)}}>
          Executa rápido a estratégia errada.
        </div>
      </div>
    </AbsoluteFill>
  );
};

const S4 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background:"#0a0a0a"}}>
      <Audio src={staticFile("audio/verdades09/c04.mp3")} volume={TTS_VOLUME}/>
      <div style={{position:"absolute",top:300,left:80,right:80}}>
        <div style={{fontFamily:FONT_STACK,color:MUTED,fontSize:36,letterSpacing:2,marginBottom:16}}>O RESULTADO</div>
        <AccentLine color={RED} delay={6}/>
        <div style={{marginTop:60,display:"flex",flexDirection:"column",gap:28}}>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:56,fontWeight:700,opacity:interpolate(f,[20,50],[0,1],CLAMP)}}>📢 Mais barulho</div>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:56,fontWeight:700,opacity:interpolate(f,[55,85],[0,1],CLAMP)}}>🎨 Mais criativo bonito</div>
          <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:56,fontWeight:700,opacity:interpolate(f,[90,120],[0,1],CLAMP)}}>📊 Mais relatório colorido</div>
        </div>
        <div style={{marginTop:60,padding:36,borderRadius:20,background:`${RED}1A`,border:`2px solid ${RED}`,opacity:interpolate(f,[140,175],[0,1],CLAMP)}}>
          <div style={{fontFamily:FONT_STACK,color:RED,fontSize:72,fontWeight:900,textAlign:"center"}}>menos VENDA</div>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const S5 = () => {
  const f = useCurrentFrame();
  return (
    <AbsoluteFill style={{background:`radial-gradient(ellipse at 50% 60%, ${GREEN}16, #000 75%)`}}>
      <Audio src={staticFile("audio/verdades09/c05.mp3")} volume={TTS_VOLUME}/>
      <div style={{position:"absolute",top:340,left:80,right:80}}>
        <div style={{fontFamily:FONT_STACK,color:GREEN,fontSize:40,letterSpacing:2,marginBottom:16}}>MISSÃO DO DIA</div>
        <AccentLine color={GREEN} delay={6}/>
        <div style={{fontFamily:FONT_STACK,color:WHITE,fontSize:66,fontWeight:800,marginTop:60,lineHeight:1.2,opacity:interpolate(f,[20,55],[0,1],CLAMP)}}>
          Antes de cobrar a agência,<br/><span style={{color:GREEN}}>audita o seu briefing</span>.
        </div>
        <div style={{marginTop:60,fontFamily:FONT_STACK,color:WHITE,fontSize:52,fontWeight:600,lineHeight:1.3,opacity:interpolate(f,[110,150],[0,1],CLAMP)}}>
          Comenta aqui 👇<br/><span style={{color:YELLOW}}>Você descreve sua oferta em 1 frase?</span>
        </div>
      </div>
    </AbsoluteFill>
  );
};

const SCENE_COMPONENTS = [S1,S2,S3,S4,S5];

export const Verdades09: React.FC = () => {
  let acc = 0;
  const timeline = SCENES.map((sc,i) => {
    const from = acc; acc += sc.dur + (i < SCENES.length-1 ? TRANSITION_DUR : 0);
    return {from, dur: sc.dur};
  });
  return (
    <AbsoluteFill style={{background:"#000"}}>
      <Audio src={staticFile("music/cafe-lofi.mp3")} volume={0.12}/>
      {SCENES.map((_,i) => {
        const C = SCENE_COMPONENTS[i];
        return (
          <Sequence key={i} from={timeline[i].from} durationInFrames={timeline[i].dur}>
            <SceneFade duration={timeline[i].dur}><C/></SceneFade>
          </Sequence>
        );
      })}
      <Noise/>
      <Vignette/>
      <FilmGrain/>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
